/**
 * Bounded residency for decoded sound-effect buffers (PRD #624 / issue #626): the
 * audio twin of the sprite-clip residency layer. The {@link AudioManager} used to
 * decode the complete {@link ../sim/content.sounds} registry on the first audio
 * gesture and keep every buffer for the life of the tab; this manager instead
 * decodes **lazily** (a sound is fetched + decoded the first time it is needed),
 * keeps a **warm set** resident (the documented common-lobby set, plus the active
 * mode's critical set - see {@link ./soundWarmSets}), tracks decoded PCM byte
 * weight + last use + pin state + active-playback references, and evicts unused
 * sounds in deterministic least-recently-used order down to a configured budget.
 *
 * Protection rules (the acceptance contract of issue #626):
 * - a sound with an **active playback reference** is never evicted;
 * - a **pinned** sound is never evicted;
 * - a **current-warm-set** member is never evicted;
 * - protected sounds may hold the total **over** the soft budget temporarily - the
 *   overage is reclaimed at the next eviction pass after protection lapses;
 * - the sound whose own `acquire` triggered an eviction pass is exempt from that
 *   one pass (it is about to be used - evicting it would churn cold plays).
 *
 * Failure is terminal and graceful: a sound whose fetch/decode fails (or whose wav
 * was never baked) is remembered as **absent** and never retried - the missing-asset
 * degrade stays a silent no-op, never a retry loop (the sprite terminal-absence
 * rule). An *evicted* sound, by contrast, reloads normally on the next acquire.
 *
 * Kept pure of Web Audio specifics: the decode boundary is an injected async
 * function and "last use" is an internal monotonic counter (not wall clock), so
 * eviction order is deterministic and the whole policy is unit-testable in plain
 * jsdom/node with a fake decoder ({@link ./soundResidency.test}).
 */

/**
 * The default soft budget for decoded sound-effect PCM, in bytes. Decoded audio is
 * float32 PCM (4 bytes per sample per channel), so short UO wavs decode to a few
 * hundred KB each; this comfortably holds the lobby warm set + a mode's critical
 * set + a working set of recently heard cues, while capping a long mixed-mode
 * session's growth. Configurable per manager (a low-power client may pass less).
 */
export const DEFAULT_SOUND_BUDGET_BYTES = 16 * 1024 * 1024;

/**
 * The decoded PCM byte weight of one buffer: samples x channels x 4 (float32).
 * A malformed buffer weighs 0 rather than poisoning the total with NaN.
 */
export function decodedPcmBytes(buffer: AudioBuffer): number {
  const bytes = buffer.length * buffer.numberOfChannels * Float32Array.BYTES_PER_ELEMENT;
  return Number.isFinite(bytes) && bytes > 0 ? bytes : 0;
}

export interface SoundResidencyOptions {
  /**
   * Fetch + decode one sound by id. Resolving `undefined` (or rejecting) marks the
   * id terminally absent - the graceful degrade for a missing/undecodable asset.
   */
  decode: (id: string) => Promise<AudioBuffer | undefined>;
  /** Soft decoded-byte budget; defaults to {@link DEFAULT_SOUND_BUDGET_BYTES}. */
  budgetBytes?: number;
  /** Byte weight of a decoded buffer; defaults to {@link decodedPcmBytes}. */
  bytesOf?: (buffer: AudioBuffer) => number;
}

interface Resident {
  buffer: AudioBuffer;
  bytes: number;
  /** Monotonic last-use stamp (bigger = more recent); deterministic LRU key. */
  lastUse: number;
  /** Live playback references (overlapping voices of the same sound stack). */
  playing: number;
  pinned: boolean;
}

export class SoundResidency {
  private readonly decode: (id: string) => Promise<AudioBuffer | undefined>;
  private readonly budgetBytes: number;
  private readonly bytesOf: (buffer: AudioBuffer) => number;
  private readonly residents = new Map<string, Resident>();
  // In-flight decodes, deduped per id so concurrent acquires share one fetch.
  private readonly loads = new Map<string, Promise<AudioBuffer | undefined>>();
  // Terminally-absent ids (missing bake / decode failure): never retried.
  private readonly absent = new Set<string>();
  private warmSet = new Set<string>();
  private clock = 0;

  constructor(opts: SoundResidencyOptions) {
    this.decode = opts.decode;
    this.budgetBytes = opts.budgetBytes ?? DEFAULT_SOUND_BUDGET_BYTES;
    this.bytesOf = opts.bytesOf ?? decodedPcmBytes;
  }

  /**
   * The resident decoded buffer for `id`, touching its last-use stamp, or
   * `undefined` when not resident (cold, evicted, or absent). Never decodes.
   */
  get(id: string): AudioBuffer | undefined {
    const entry = this.residents.get(id);
    if (!entry) return undefined;
    entry.lastUse = ++this.clock;
    return entry.buffer;
  }

  /**
   * The decoded buffer for `id`, lazily fetching + decoding it on first use (deduped
   * across concurrent calls), or `undefined` for a terminally absent sound. Runs an
   * eviction pass afterwards - exempting `id` itself, which is about to be used.
   */
  async acquire(id: string): Promise<AudioBuffer | undefined> {
    const resident = this.residents.get(id);
    if (resident) {
      resident.lastUse = ++this.clock;
      this.evictToBudget(id);
      return resident.buffer;
    }
    if (this.absent.has(id)) return undefined;
    let load = this.loads.get(id);
    if (!load) {
      load = this.decode(id)
        .catch(() => undefined)
        .then((buffer) => {
          this.loads.delete(id);
          if (!buffer) {
            this.absent.add(id);
            return undefined;
          }
          this.residents.set(id, {
            buffer,
            bytes: this.bytesOf(buffer),
            lastUse: ++this.clock,
            playing: 0,
            pinned: false,
          });
          this.evictToBudget(id);
          return buffer;
        });
      this.loads.set(id, load);
    }
    return load;
  }

  /**
   * Replace the current warm set and decode every member (best effort; an absent
   * member degrades silently). Old members lose their protection and become
   * ordinary LRU-evictable residents - the mode-transition semantics. Resolves
   * once every member's decode has settled (tests await it; callers may not).
   */
  async setWarmSet(ids: readonly string[]): Promise<void> {
    this.warmSet = new Set(ids);
    await Promise.all(ids.map((id) => this.acquire(id)));
    this.evictToBudget();
  }

  /** Pin `id` against eviction (an active-feature hold beyond the warm set). */
  pin(id: string): void {
    const entry = this.residents.get(id);
    if (entry) entry.pinned = true;
  }

  /** Release a pin; the sound becomes ordinary LRU-evictable again. */
  unpin(id: string): void {
    const entry = this.residents.get(id);
    if (entry) entry.pinned = false;
  }

  /** Record one live playback voice of `id` (overlapping voices stack). */
  beginPlayback(id: string): void {
    const entry = this.residents.get(id);
    if (entry) entry.playing++;
  }

  /**
   * Record a playback voice of `id` ending. When the last voice ends, any budget
   * overage the playing protection was holding open is reclaimed.
   */
  endPlayback(id: string): void {
    const entry = this.residents.get(id);
    if (!entry) return;
    entry.playing = Math.max(0, entry.playing - 1);
    if (entry.playing === 0) this.evictToBudget();
  }

  /** Total decoded PCM bytes currently resident. */
  totalBytes(): number {
    let total = 0;
    for (const entry of this.residents.values()) total += entry.bytes;
    return total;
  }

  /** The resident sound ids, in insertion order (for tests/metrics). */
  residentIds(): string[] {
    return [...this.residents.keys()];
  }

  /**
   * Evict unprotected residents in least-recently-used order until the total is
   * within budget (or nothing evictable remains - playing/pinned/warm-set members
   * are skipped and may hold the total over the soft budget temporarily).
   * `exempt` shields the sound whose own acquire triggered this pass.
   */
  private evictToBudget(exempt?: string): void {
    let total = this.totalBytes();
    if (total <= this.budgetBytes) return;
    const candidates = [...this.residents.entries()]
      .filter(
        ([id, entry]) =>
          id !== exempt && !entry.pinned && entry.playing === 0 && !this.warmSet.has(id),
      )
      .sort((a, b) => a[1].lastUse - b[1].lastUse);
    for (const [id, entry] of candidates) {
      if (total <= this.budgetBytes) return;
      this.residents.delete(id);
      total -= entry.bytes;
    }
  }
}
